import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { machinesApi } from '../../api/client';
import { Machine } from '../../types';
import { Printer, CheckCircle2, XCircle, Loader2, AlertTriangle } from 'lucide-react';

interface MachineSelectorProps {
  stepId: number;
  currentMachineId?: number;
  department?: string;
  onAssigned?: (machine: Machine) => void;
}

export function MachineSelector({ stepId, currentMachineId, department, onAssigned }: MachineSelectorProps) {
  const queryClient = useQueryClient();
  const [assigningId, setAssigningId] = useState<number | null>(null);
  const [successId, setSuccessId] = useState<number | null>(null);

  const { data: machines, isLoading, isError } = useQuery<Machine[]>({
    queryKey: ['machines', department ?? 'IMPRENTA'],
    queryFn: () => machinesApi.list((department ?? 'IMPRENTA').toLowerCase()),
    staleTime: 30_000,
    retry: 1,
  });

  const assignMutation = useMutation({
    mutationFn: ({ mId }: { mId: number }) => machinesApi.assignToStep(stepId, mId),
    onSuccess: (_data, { mId }) => {
      setSuccessId(mId);
      setAssigningId(null);
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      const machine = machines?.find(m => m.id === mId);
      if (machine && onAssigned) onAssigned(machine);
      setTimeout(() => setSuccessId(null), 2500);
    },
    onError: () => {
      setAssigningId(null);
    },
  });

  const handleAssign = (machine: Machine) => {
    if (!machine.isAvailable || assignMutation.isPending) return;
    setAssigningId(machine.id);
    assignMutation.mutate({ mId: machine.id });
  };

  if (isLoading) {
    return (
      <div style={{
        background: '#12121a',
        border: '1px solid #1e1e2e',
        borderRadius: 14,
        padding: '20px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        color: '#6b6880',
        fontSize: 13,
        fontFamily: 'DM Sans, sans-serif',
      }}>
        <Loader2 size={16} color="#f97316" style={{ animation: 'spin 0.8s linear infinite' }} />
        Cargando máquinas...
      </div>
    );
  }

  if (isError) {
    return (
      <div style={{
        background: 'rgba(239,68,68,0.06)',
        border: '1px solid rgba(239,68,68,0.2)',
        borderRadius: 14,
        padding: '16px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        color: '#f87171',
        fontSize: 13,
        fontFamily: 'DM Sans, sans-serif',
      }}>
        <AlertTriangle size={15} />
        No se pudieron cargar las máquinas.
      </div>
    );
  }

  const list = machines ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Section title */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 7,
        marginBottom: 2,
      }}>
        <Printer size={14} color="#f97316" />
        <span style={{
          fontSize: 10,
          fontFamily: 'JetBrains Mono, monospace',
          fontWeight: 700,
          letterSpacing: '0.08em',
          color: '#8b89a0',
        }}>
          MÁQUINAS DISPONIBLES
        </span>
      </div>

      {list.length === 0 ? (
        <div style={{
          background: '#12121a',
          border: '1px solid #1e1e2e',
          borderRadius: 12,
          padding: '18px 20px',
          textAlign: 'center',
          color: '#4a4858',
          fontSize: 13,
          fontFamily: 'DM Sans, sans-serif',
        }}>
          No hay máquinas registradas para {(department ?? 'imprenta').toLowerCase()}.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {list.map((machine) => {
            const isSelected = machine.id === currentMachineId;
            const isAssigning = assigningId === machine.id;
            const isSuccess = successId === machine.id;
            const disabled = !machine.isAvailable || assignMutation.isPending;

            return (
              <div
                key={machine.id}
                onClick={() => !disabled && handleAssign(machine)}
                style={{
                  background: isSelected
                    ? 'rgba(249,115,22,0.08)'
                    : '#12121a',
                  border: isSelected
                    ? '1px solid rgba(249,115,22,0.35)'
                    : isSuccess
                    ? '1px solid rgba(34,197,94,0.35)'
                    : '1px solid #1e1e2e',
                  borderRadius: 12,
                  padding: '14px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: !machine.isAvailable && !isSelected ? 0.6 : 1,
                  transition: 'all 0.18s cubic-bezier(0.16,1,0.3,1)',
                  boxShadow: isSelected ? '0 0 14px rgba(249,115,22,0.1)' : 'none',
                }}
                onMouseEnter={e => {
                  if (!disabled)
                    e.currentTarget.style.borderColor = isSelected ? 'rgba(249,115,22,0.5)' : '#2e2e42';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = isSelected
                    ? 'rgba(249,115,22,0.35)'
                    : isSuccess
                    ? 'rgba(34,197,94,0.35)'
                    : '#1e1e2e';
                }}
                role="button"
                aria-label={`Asignar máquina ${machine.name}`}
                aria-pressed={isSelected}
              >
                {/* Left: icon + info */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
                  <div style={{
                    width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                    background: machine.isAvailable
                      ? 'rgba(34,197,94,0.08)'
                      : 'rgba(239,68,68,0.08)',
                    border: machine.isAvailable
                      ? '1px solid rgba(34,197,94,0.2)'
                      : '1px solid rgba(239,68,68,0.2)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Printer
                      size={17}
                      color={machine.isAvailable ? '#22c55e' : '#ef4444'}
                    />
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: isSelected ? '#f97316' : '#e8e6f4',
                      fontFamily: 'DM Sans, sans-serif',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      {machine.name}
                    </div>
                    {machine.description && (
                      <div style={{
                        fontSize: 11,
                        color: '#6b6880',
                        fontFamily: 'DM Sans, sans-serif',
                        marginTop: 2,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>
                        {machine.description}
                      </div>
                    )}
                  </div>
                </div>

                {/* Right: status badge + action */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  {/* Availability badge */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    padding: '3px 8px', borderRadius: 6,
                    background: machine.isAvailable
                      ? 'rgba(34,197,94,0.1)'
                      : 'rgba(239,68,68,0.1)',
                    border: machine.isAvailable
                      ? '1px solid rgba(34,197,94,0.25)'
                      : '1px solid rgba(239,68,68,0.25)',
                  }}>
                    <div style={{
                      width: 5, height: 5, borderRadius: '50%',
                      background: machine.isAvailable ? '#22c55e' : '#ef4444',
                      boxShadow: machine.isAvailable
                        ? '0 0 5px rgba(34,197,94,0.6)'
                        : 'none',
                    }} />
                    <span style={{
                      fontSize: 9,
                      fontFamily: 'JetBrains Mono, monospace',
                      fontWeight: 700,
                      letterSpacing: '0.06em',
                      color: machine.isAvailable ? '#22c55e' : '#ef4444',
                    }}>
                      {machine.isAvailable ? 'LIBRE' : 'OCUPADA'}
                    </span>
                  </div>

                  {/* Action button / status */}
                  {isAssigning ? (
                    <Loader2 size={16} color="#f97316" style={{ animation: 'spin 0.8s linear infinite' }} />
                  ) : isSuccess ? (
                    <CheckCircle2 size={16} color="#22c55e" />
                  ) : isSelected ? (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 4,
                      padding: '4px 10px', borderRadius: 7,
                      background: 'rgba(249,115,22,0.15)',
                      border: '1px solid rgba(249,115,22,0.3)',
                      fontSize: 10,
                      fontFamily: 'JetBrains Mono, monospace',
                      fontWeight: 700,
                      color: '#f97316',
                      letterSpacing: '0.04em',
                    }}>
                      ASIGNADA
                    </div>
                  ) : machine.isAvailable ? (
                    <div style={{
                      padding: '4px 10px', borderRadius: 7,
                      background: 'transparent',
                      border: '1px solid #2a2838',
                      fontSize: 10,
                      fontFamily: 'JetBrains Mono, monospace',
                      fontWeight: 600,
                      color: '#6b6880',
                      letterSpacing: '0.04em',
                    }}>
                      ASIGNAR
                    </div>
                  ) : (
                    <XCircle size={15} color="#ef4444" style={{ opacity: 0.6 }} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Error feedback */}
      {assignMutation.isError && (
        <div style={{
          background: 'rgba(239,68,68,0.08)',
          border: '1px solid rgba(239,68,68,0.2)',
          borderRadius: 8,
          padding: '10px 14px',
          fontSize: 12,
          color: '#f87171',
          fontFamily: 'DM Sans, sans-serif',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}>
          <AlertTriangle size={13} />
          Error al asignar la máquina. Inténtalo de nuevo.
        </div>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
